//document type mapping
export const mapCategoryToDocumentType = (
  category: string,
  isIndividualRoute: boolean
): string => {
  switch (category) {
    case "businessProfile":
      return "company_proof";
    case "shareHoldersDirectorsRegisters":
      return "company_proof";
    case "proofOfAddress":
      return isIndividualRoute ? "utility_bill" : "company_address_proof";
    default:
      return "-"; // Default fallback
  }
};

export const formatLocalDate = (dateString: string) => {
  if (!dateString) return "";

  // If it's already in DD/MM/YYYY format (or contains slashes), return it directly
  if (dateString.includes("/")) {
    return dateString;
  }

  // Otherwise try to parse it as a standard date string
  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return the original string if parsing failed
    }

    // Format using the browser's locale and timezone settings
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    // If anything goes wrong, return the original string
    return dateString;
  }
};

export const deduplicateCountries = (countryString: string): string => {
  // Split by commas, handle the case where countries are concatenated without commas
  let countries = countryString
    .replace(/([A-Z]{2})([A-Z]{2})/g, "$1,$2") // Insert comma between adjacent ISO codes
    .split(",")
    .map((country) => country.trim())
    .filter((country) => country && country !== "-"); // Remove empty entries and '-'

  // Remove duplicates by using a Set
  return Array.from(new Set(countries)).join(", ");
};

export const getNameFromPath = (path: string) => {
  const parts = path.split(`\\`);
  const extractedName = parts.slice(1).join(`\\`);

  return extractedName;
};

export const getLocalPhotoIdentifier = (userId: string) => {
  return `local_photo_${userId}`;
};

export const getFilteredDocuments = (
  documents: string[],
  hasBeenSentToWallex: boolean,
  uploadedFiles: string[] | undefined,
  excludedFiles: string[] | undefined,
  includeExcluded = false
): string[] => {
  if (hasBeenSentToWallex) {
    // If documents have been sent but uploadedFiles is empty,
    // just show all non-excluded docs as a fallback
    if (!uploadedFiles || uploadedFiles.length === 0) {
      return documents.filter((doc) => !(excludedFiles ?? []).includes(doc));
    }

    // When documents have been sent, only show documents that were actually uploaded to Wallex
    return documents.filter((doc) => {
      // Extract just the base filename from the full path
      const fullPath = getNameFromPath(doc);

      // Get the last part after the last backslash or forward slash
      let fileName = fullPath;
      if (fullPath.includes("\\")) {
        fileName = fullPath.split("\\").pop() || fullPath;
      } else if (fullPath.includes("/")) {
        fileName = fullPath.split("/").pop() || fullPath;
      }

      // Check if this file exists in uploadedFiles from backend
      const isMatch = uploadedFiles.some((uploadedFile) => {
        // Use strict equality for matching
        return fileName === uploadedFile;
      });

      return isMatch;
    });
  }

  // If we haven't sent to Wallex yet, handle based on includeExcluded parameter
  if (includeExcluded) {
    // Return all documents, even excluded ones
    return documents;
  } else {
    // Only return non-excluded documents
    return documents.filter((doc) => !(excludedFiles ?? []).includes(doc));
  }
};
